import React, { useMemo, useState } from "react";
import { Fade, Slide, Zoom } from "react-awesome-reveal";
import {
  FaChartLine,
  FaCheckCircle,
  FaChevronRight,
  FaClipboardCheck,
  FaDumbbell,
  FaEye,
  FaMedal,
  FaRocket,
  FaTrophy,
  FaUserCheck,
  FaUserFriends,
  FaUsers,
} from "react-icons/fa";
import { GiKimono } from "react-icons/gi";
import useScrollToTop from "./../../hooks/useScollToTop";

const COACH_FEATURES = [
  {
    id: "groups",
    icon: FaUsers,
    eyebrow: "Organisation",
    title: "Mes groupes & invitations",
    subtitle: "Créer, gérer et inviter les judokas",
    description:
      "L’espace groupes permet au coach d’organiser ses cours, de créer des groupes d’entraînement, d’inviter les judokas et de suivre les demandes d’inscription.",
    image: "/img/diaporama/Page_Coach_Groupes.png",
    accent: "from-orange-500 to-amber-400",
    soft: "bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-200",
    ring: "border-orange-200 dark:border-orange-500/20",
    bullets: [
      "Création de groupes par niveau, âge ou créneau",
      "Invitation des judokas par lien ou QR code",
      "Validation des demandes d’inscription",
    ],
  },
  {
    id: "results",
    icon: FaTrophy,
    eyebrow: "Compétition",
    title: "Résultats & palmarès",
    subtitle: "Suivre les résultats des groupes",
    description:
      "Le coach retrouve les résultats de ses judokas, les podiums obtenus, l’historique des compétitions et les performances réalisées tout au long de la saison.",
    image: "/img/diaporama/Page_Coach_Resultats.png",
    accent: "from-sky-500 to-blue-600",
    soft: "bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200",
    ring: "border-sky-200 dark:border-sky-500/20",
    bullets: [
      "Résultats individuels des judokas",
      "Palmarès et podiums par groupe",
      "Historique des compétitions de la saison",
    ],
  },
  {
    id: "grades",
    icon: FaClipboardCheck,
    eyebrow: "Progression",
    title: "Passage de grade des judokas",
    subtitle: "Suivre les ceintures et objectifs",
    description:
      "Le suivi des grades permet de visualiser la progression technique de chaque judoka, son historique de ceintures et les prochaines étapes à préparer.",
    image: "/img/diaporama/Page_Coach_Passages_grades.png",
    accent: "from-purple-500 to-indigo-600",
    soft: "bg-purple-50 text-purple-700 dark:bg-purple-500/10 dark:text-purple-200",
    ring: "border-purple-200 dark:border-purple-500/20",
    bullets: [
      "Historique des ceintures",
      "Suivi individuel par judoka",
      "Préparation des prochains objectifs",
    ],
  },
  {
    id: "statistics",
    icon: FaChartLine,
    eyebrow: "Analyse",
    title: "Statistiques des groupes",
    subtitle: "Analyser la progression collective",
    description:
      "Les statistiques donnent au coach une vision globale de ses groupes : progression, résultats, participation, régularité et évolution des judokas.",
    image: "/img/diaporama/Page_Coach_Statistiques.png",
    accent: "from-emerald-500 to-teal-600",
    soft: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200",
    ring: "border-emerald-200 dark:border-emerald-500/20",
    bullets: [
      "Vue synthétique par groupe",
      "Indicateurs de progression",
      "Analyse de la saison sportive",
    ],
  },
  {
    id: "attendance",
    icon: FaUserCheck,
    eyebrow: "Présences",
    title: "Présence des judokas",
    subtitle: "Suivre la présence et l’assiduité",
    description:
      "Le module de présence permet de suivre l’assiduité des judokas aux cours, d’identifier les absences répétées et d’accompagner plus régulièrement chaque élève.",
    image: "/img/diaporama/Page_Coach_Appels.png",
    accent: "from-cyan-500 to-sky-600",
    soft: "bg-cyan-50 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-200",
    ring: "border-cyan-200 dark:border-cyan-500/20",
    bullets: [
      "Feuille de présence par groupe",
      "Historique des présences",
      "Suivi de l’assiduité des judokas",
    ],
  },
  {
    id: "physical",
    icon: FaDumbbell,
    eyebrow: "Préparation",
    title: "Préparation physique",
    subtitle: "Attribuer du travail physique ciblé",
    description:
      "Le coach peut créer des exercices de préparation physique, les attribuer à un ou plusieurs judokas ou à un groupe entier, puis suivre les validations effectuées.",
    image: "/img/diaporama/Page_Coach_Prepa.png",
    accent: "from-rose-500 to-orange-500",
    soft: "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-200",
    ring: "border-rose-200 dark:border-rose-500/20",
    bullets: [
      "Création d’exercices personnalisés",
      "Attribution à un judoka ou à un groupe",
      "Validation des exercices par les judokas",
    ],
  },
];

const FUTURE_FEATURES = [
  {
    icon: FaMedal,
    title: "Évaluations de grade",
    text: "Une future fonctionnalité permettra au coach d’évaluer adhérents lors des passages de grade. Il pourra déterminer les techniques à travailler pour chaque ceinture et attribue des notes d’évaluation pour suivre la progression technique de ses judokas.",
    gradient: "from-orange-500 to-amber-400",
  },
  {
    icon: GiKimono,
    title: "Fonctionnalités personnalisées",
    text: "N'hésitez pas à me communiquer les fonctionnalités que vous aimeriez voir dans l’espace coach. L’objectif est de construire un outil d’accompagnement complet pour les clubs de judo, alors vos retours sont précieux !",
    gradient: "from-sky-500 to-indigo-500",
  },
];

function ScreenshotPreview({ src, alt, activeFeature }) {
  const [hasError, setHasError] = useState(false);

  if (hasError || !src) {
    return (
      <div className="relative flex aspect-[16/9] w-full items-center justify-center overflow-hidden rounded-[2rem] border border-dashed border-slate-300 bg-slate-100 text-center dark:border-white/10 dark:bg-white/[0.04]">
        <div
          className={`absolute inset-0 bg-gradient-to-br ${activeFeature.accent} opacity-10`}
        />

        <div className="relative">
          <FaEye className="mx-auto text-3xl text-slate-400 dark:text-white/35" />
          <p className="mt-3 text-sm font-bold text-slate-600 dark:text-white/60">
            Capture d’écran à ajouter
          </p>
          <p className="mt-1 text-xs font-medium text-slate-400 dark:text-white/35">
            Format conseillé : paysage 16/9
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-white p-2 shadow-2xl shadow-slate-300/70 dark:border-white/10 dark:bg-white/10 dark:shadow-black/30">
      <div
        className={`absolute -inset-8 bg-gradient-to-br ${activeFeature.accent} opacity-20 blur-3xl`}
      />

      <div className="relative overflow-hidden rounded-[1.6rem] bg-slate-100 dark:bg-slate-950">
        <img
          src={src}
          alt={alt}
          onError={() => setHasError(true)}
          className="aspect-[16/9] w-full object-cover"
        />

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/75 to-transparent p-5">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-2 text-xs font-bold uppercase tracking-[0.14em] text-white backdrop-blur-xl">
            <activeFeature.icon />
            Aperçu du module
          </div>
        </div>
      </div>
    </div>
  );
}

function EspaceCoach() {
  useScrollToTop();
  const [activeFeatureId, setActiveFeatureId] = useState(COACH_FEATURES[0].id);

  const activeFeature = useMemo(() => {
    return (
      COACH_FEATURES.find((feature) => feature.id === activeFeatureId) ||
      COACH_FEATURES[0]
    );
  }, [activeFeatureId]);

  const ActiveIcon = activeFeature.icon;

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-50 pt-[88px] text-slate-950 dark:bg-slate-950 dark:text-white lg:pt-[104px]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[-140px] top-20 h-80 w-80 rounded-full bg-orange-300/20 blur-3xl dark:bg-orange-500/10" />
        <div className="absolute right-[-120px] top-[420px] h-96 w-96 rounded-full bg-sky-300/25 blur-3xl dark:bg-sky-500/10" />
        <div className="absolute bottom-20 left-1/3 h-96 w-96 rounded-full bg-emerald-300/20 blur-3xl dark:bg-emerald-500/10" />
      </div>

      <section className="relative mx-auto w-full max-w-[1280px] px-4 py-10 sm:px-6 lg:px-8">
        <Fade triggerOnce direction="up" duration={700}>
          <div className="relative overflow-hidden rounded-[2.3rem] border border-blue-300/20 bg-gradient-to-br from-sky-500 via-blue-600 to-slate-950 shadow-[0_30px_80px_-20px_rgba(15,23,42,0.55)]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.18),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.08),transparent_24%)]" />
            <div className="absolute -left-16 top-10 h-40 w-40 rounded-full bg-cyan-300/20 blur-3xl" />
            <div className="absolute right-0 top-0 h-52 w-52 rounded-full bg-blue-300/10 blur-3xl" />

            <div className="relative p-6 sm:p-8 lg:p-10">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-sky-100 backdrop-blur-xl">
                    <FaUserFriends />
                    Espace coach
                  </div>

                  <h1 className="mt-4 max-w-4xl font-heading text-3xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
                    Des outils visuels pour suivre vos groupes et accompagner
                    vos judokas.
                  </h1>
                </div>

                {/* <p className="max-w-xl text-sm font-medium leading-7 text-blue-50/85 sm:text-base">
                  Une présentation claire des modules coach de Judo Coach Pro :
                  groupes, résultats, grades, statistiques, présences et
                  préparation physique.
                </p> */}
              </div>
            </div>
          </div>
        </Fade>
      </section>

      <section className="relative mx-auto w-full max-w-[1280px] px-4 pb-16 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
          <Slide triggerOnce direction="left" duration={650}>
            <aside className="lg:sticky lg:top-[120px] lg:self-start">
              <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl shadow-slate-200/70 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/30">
                <div
                  className={`bg-gradient-to-br ${activeFeature.accent} p-5 text-white`}
                >
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/70">
                    Fonctionnalités coach
                  </p>

                  <h2 className="mt-2 font-heading text-2xl font-bold">
                    Choisissez un module
                  </h2>
                </div>

                <div className="space-y-2 p-4">
                  {COACH_FEATURES.map((feature) => {
                    const Icon = feature.icon;
                    const isActive = activeFeatureId === feature.id;

                    return (
                      <button
                        key={feature.id}
                        type="button"
                        onClick={() => setActiveFeatureId(feature.id)}
                        className={`group flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition ${
                          isActive
                            ? `${feature.ring} ${feature.soft} shadow-lg`
                            : "border-slate-200 bg-slate-50 text-slate-700 hover:-translate-y-0.5 hover:bg-white hover:shadow-md dark:border-white/10 dark:bg-white/[0.04] dark:text-white/70 dark:hover:bg-white/[0.08]"
                        }`}
                      >
                        <div
                          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl transition ${
                            isActive
                              ? "bg-white/70 text-current shadow-sm dark:bg-white/10"
                              : "bg-white text-slate-500 group-hover:text-orange-600 dark:bg-white/10 dark:text-white/45"
                          }`}
                        >
                          <Icon />
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-bold">
                            {feature.title}
                          </p>
                          <p className="truncate text-xs font-semibold opacity-70">
                            {feature.subtitle}
                          </p>
                        </div>

                        <FaChevronRight
                          className={`shrink-0 text-xs transition ${
                            isActive
                              ? "translate-x-0 text-current"
                              : "text-slate-400 group-hover:translate-x-1 group-hover:text-orange-500 dark:text-white/30"
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            </aside>
          </Slide>

          <div className="space-y-6">
            <Fade
              triggerOnce
              direction="up"
              duration={700}
              key={activeFeature.id}
            >
              <article className="overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white shadow-2xl shadow-slate-200/70 dark:border-white/10 dark:bg-slate-900 dark:shadow-black/30">
                <div
                  className={`h-2 bg-gradient-to-r ${activeFeature.accent}`}
                />

                <div className="p-4 sm:p-6">
                  <ScreenshotPreview
                    src={activeFeature.image}
                    alt={activeFeature.title}
                    activeFeature={activeFeature}
                  />

                  <div className="mt-7 grid gap-7 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
                    <div>
                      <Zoom triggerOnce duration={450}>
                        <div
                          className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] ${activeFeature.soft} ${activeFeature.ring}`}
                        >
                          <ActiveIcon />
                          {activeFeature.eyebrow}
                        </div>
                      </Zoom>

                      <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-4xl">
                        {activeFeature.title}
                      </h2>

                      <p className="mt-3 text-lg font-bold text-slate-700 dark:text-white/80">
                        {activeFeature.subtitle}
                      </p>

                      <p className="mt-4 text-sm font-medium leading-8 text-slate-600 dark:text-white/60 sm:text-base">
                        {activeFeature.description}
                      </p>
                    </div>

                    <div className="grid gap-3">
                      {activeFeature.bullets.map((bullet, index) => (
                        <Fade
                          key={bullet}
                          triggerOnce
                          direction="up"
                          delay={index * 80}
                          duration={450}
                        >
                          <div className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 transition hover:-translate-y-0.5 hover:bg-white hover:shadow-lg dark:border-white/10 dark:bg-white/[0.04] dark:hover:bg-white/[0.08]">
                            <div
                              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${activeFeature.soft}`}
                            >
                              <FaCheckCircle />
                            </div>

                            <p className="text-sm font-bold text-slate-700 dark:text-white/75">
                              {bullet}
                            </p>
                          </div>
                        </Fade>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            </Fade>

            <Fade triggerOnce direction="up" duration={700}>
              <section className="overflow-hidden rounded-[2.2rem] border border-slate-200 bg-white shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
                <div className="grid lg:grid-cols-[0.9fr_1.1fr]">
                  <div className="relative overflow-hidden bg-slate-950 p-6 text-white">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(249,115,22,0.35),transparent_35%),radial-gradient(circle_at_80%_20%,rgba(59,130,246,0.25),transparent_30%)]" />

                    <div className="relative">
                      <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-orange-200 backdrop-blur-xl">
                        <FaRocket />
                      </div>

                      <p className="mt-5 text-xs font-bold uppercase tracking-[0.18em] text-orange-200">
                        Prochaines évolutions
                      </p>

                      <h2 className="mt-2 font-heading text-2xl font-bold">
                        Vers un accompagnement encore plus précis
                      </h2>

                      <p className="mt-3 text-sm font-medium leading-7 text-white/65">
                        L’espace coach pourra évoluer vers une vraie grille
                        d’évaluation technique pour préparer les passages de
                        grade et individualiser les objectifs.
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-3 p-5 sm:grid-cols-2">
                    {FUTURE_FEATURES.map((item, index) => {
                      const Icon = item.icon;

                      return (
                        <Fade
                          key={item.title}
                          triggerOnce
                          direction="up"
                          delay={index * 120}
                        >
                          <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 transition hover:-translate-y-0.5 hover:bg-white hover:shadow-lg dark:border-white/10 dark:bg-white/[0.04] dark:hover:bg-white/[0.08]">
                            <div
                              className={`flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br ${item.gradient} text-white shadow-lg`}
                            >
                              <Icon />
                            </div>

                            <p className="mt-3 text-sm font-bold text-slate-950 dark:text-white">
                              {item.title}
                            </p>

                            <p className="mt-1 text-xs font-medium leading-5 text-slate-500 dark:text-white/50">
                              {item.text}
                            </p>
                          </div>
                        </Fade>
                      );
                    })}
                  </div>
                </div>
              </section>
            </Fade>
          </div>
        </div>
      </section>
    </main>
  );
}

export default EspaceCoach;
